/**
 * Template de prise de besoin : Politique d'utilisation de l'IA v1
 * Aligné sur NIST AI RMF, ITIL 4, COBIT 2019 et ISO 27001
 * Version complète couvrant tous les aspects d'une politique d'utilisation de l'IA
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueIaV1: IntakeTemplate = {
  id: 'politique-ia-v1',
  name: 'Prise de besoin Politique d\'utilisation de l\'IA v1',
  description:
    'Questionnaire complet pour élaborer une politique d\'utilisation de l\'IA alignée sur NIST AI RMF, ITIL 4, COBIT 2019 et ISO 27001. Couvre : gouvernance IA, cartographie des risques, mesure et gestion des risques, utilisations autorisées, éthique, conformité, et responsabilités.',
  documentTypeCode: 'POLITIQUE_IA',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Gouvernance IA',
      description: 'Informations sur l\'entreprise et structure de gouvernance IA',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la politique IA. Cette information est essentielle pour personnaliser la politique selon le contexte organisationnel.',
        },
        {
          id: 'secteur_activite',
          label: 'Secteur d\'activité',
          type: 'text',
          placeholder: 'Ex: Manufacturier, Services financiers, Santé, Éducation...',
          helpText: 'Le secteur d\'activité influence les risques et obligations réglementaires spécifiques à l\'IA (ex: santé = données sensibles, finance = conformité stricte).',
        },
        {
          id: 'utilisation_ia_actuelle',
          label: 'Utilisez-vous actuellement des outils d\'IA dans votre organisation ?',
          type: 'select',
          options: ['Oui, utilisation active', 'Oui, projets pilotes', 'Non, mais planifié', 'Non, pas prévu'],
          required: true,
          helpText: 'NIST AI RMF (GOVERN): Évaluation de la maturité actuelle en IA pour adapter la politique selon le niveau d\'adoption.',
        },
        {
          id: 'outils_ia_utilises',
          label: 'Quels outils d\'IA sont actuellement utilisés ou prévus ?',
          type: 'multiselect',
          options: [
            'ChatGPT / GPT-4',
            'Copilot (GitHub, Microsoft)',
            'Claude (Anthropic)',
            'Midjourney / DALL-E',
            'Google Bard / Gemini',
            'Outils de traitement de données',
            'Automatisation de processus',
            'Reconnaissance vocale / visuelle',
            'Autre',
          ],
          helpText: 'NIST AI RMF (MAP): Identification des systèmes IA pour cartographier les risques spécifiques à chaque outil.',
        },
        {
          id: 'structure_gouvernance_ia',
          label: 'Quelle structure de gouvernance souhaitez-vous pour l\'IA ?',
          type: 'select',
          options: [
            'Comité de gouvernance IA dédié',
            'Intégration dans le comité TI existant',
            'Responsable IA désigné',
            'Gouvernance distribuée par département',
            'À déterminer',
          ],
          helpText: 'NIST AI RMF (GOVERN-1): Structure de gouvernance claire essentielle pour la gestion des risques IA et la prise de décision.',
        },
        {
          id: 'responsable_ia',
          label: 'Qui sera responsable de la gouvernance et de la conformité IA ?',
          type: 'text',
          placeholder: 'Ex: Directeur TI, Responsable Cybersécurité, DPO...',
          helpText: 'COBIT 2019 (APO12): Attribution claire des responsabilités pour la gestion des risques IA.',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique IA ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des utilisateurs. Un ton formel assure la conformité légale, tandis qu\'un ton pédagogique favorise la compréhension des enjeux IA.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Cartographie des Risques IA (NIST AI RMF MAP)',
      description: 'Identification et cartographie des risques liés à l\'utilisation de l\'IA',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'cas_usage_ia_identifies',
          label: 'Quels sont les cas d\'usage IA identifiés dans votre organisation ?',
          type: 'multiselect',
          options: [
            'Rédaction et génération de contenu',
            'Analyse de données et rapports',
            'Support client (chatbots)',
            'Automatisation de processus',
            'Reconnaissance d\'images / vidéos',
            'Traitement de langage naturel',
            'Prédiction et analyse prédictive',
            'Recommandations personnalisées',
            'Recherche et veille',
            'Autre',
          ],
          helpText: 'NIST AI RMF (MAP-1): Identification des cas d\'usage pour évaluer les risques spécifiques à chaque contexte d\'utilisation.',
        },
        {
          id: 'donnees_utilisees_ia',
          label: 'Quels types de données seront utilisés avec les outils IA ?',
          type: 'multiselect',
          options: [
            'Données publiques / non sensibles',
            'Renseignements personnels',
            'Données financières',
            'Propriété intellectuelle',
            'Secrets commerciaux',
            'Données clients',
            'Données RH',
            'Données médicales',
            'Autre',
          ],
          helpText: 'NIST AI RMF (MAP-2): Classification des données pour évaluer les risques de confidentialité et conformité (Loi 25, RGPD).',
        },
        {
          id: 'risques_ia_identifies',
          label: 'Quels risques IA avez-vous identifiés ou anticipez-vous ?',
          type: 'multiselect',
          options: [
            'Fuites de données / Confidentialité',
            'Biais algorithmiques / Discrimination',
            'Hallucinations / Erreurs de l\'IA',
            'Dépendance excessive à l\'IA',
            'Perte de contrôle sur les données',
            'Violation de propriété intellectuelle',
            'Non-conformité réglementaire',
            'Risques réputationnels',
            'Sécurité des systèmes IA',
            'Autre',
          ],
          helpText: 'NIST AI RMF (MAP-3): Cartographie complète des risques pour établir des mesures de mitigation appropriées.',
        },
        {
          id: 'niveau_risque_acceptable',
          label: 'Quel niveau de risque IA est acceptable pour votre organisation ?',
          type: 'select',
          options: [
            'Risque très faible (utilisation très restrictive)',
            'Risque faible (utilisation contrôlée)',
            'Risque modéré (utilisation avec garde-fous)',
            'Risque élevé acceptable (utilisation large avec monitoring)',
          ],
          helpText: 'NIST AI RMF (MAP-4): Définition de l\'appétit au risque pour guider les décisions d\'autorisation d\'utilisation.',
        },
        {
          id: 'groupes_utilisateurs_ia',
          label: 'Quels groupes d\'utilisateurs utiliseront l\'IA ?',
          type: 'multiselect',
          options: [
            'Tous les employés',
            'Direction / Gestionnaires',
            'Équipe TI / Développeurs',
            'Équipe Marketing / Communication',
            'Équipe RH',
            'Équipe Finances',
            'Consultants externes',
            'Autre',
          ],
          helpText: 'ITIL 4: Identification des groupes d\'utilisateurs pour adapter les règles et restrictions selon les besoins et risques.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Mesure & Gestion des Risques IA (NIST AI RMF MEASURE & MANAGE)',
      description: 'Contrôles, mesures et processus de gestion des risques IA',
      icon: 'Shield',
      questions: [
        {
          id: 'outils_ia_autorises',
          label: 'Quels outils IA seront autorisés pour utilisation professionnelle ?',
          type: 'multiselect',
          options: [
            'ChatGPT (version gratuite)',
            'ChatGPT Plus / Enterprise',
            'Microsoft Copilot',
            'GitHub Copilot',
            'Claude (Anthropic)',
            'Google Gemini / Bard',
            'Midjourney / DALL-E',
            'Outils internes développés',
            'Tous les outils publics',
            'À déterminer',
          ],
          helpText: 'NIST AI RMF (MANAGE-1): Catalogue des outils IA autorisés pour contrôler l\'utilisation et réduire les risques.',
        },
        {
          id: 'restrictions_utilisation_ia',
          label: 'Quelles restrictions souhaitez-vous imposer sur l\'utilisation de l\'IA ?',
          type: 'multiselect',
          options: [
            'Interdiction d\'utiliser des données sensibles',
            'Interdiction d\'utiliser des données clients',
            'Interdiction d\'utiliser des données financières',
            'Interdiction d\'utiliser des données RH',
            'Obligation de révision humaine',
            'Obligation de validation avant publication',
            'Interdiction de génération de code en production',
            'Interdiction d\'utilisation personnelle',
            'Aucune restriction spécifique',
          ],
          helpText: 'NIST AI RMF (MANAGE-2): Restrictions claires pour protéger les données sensibles et réduire les risques.',
        },
        {
          id: 'processus_approbation_ia',
          label: 'Quel processus d\'approbation souhaitez-vous pour l\'utilisation de nouveaux outils IA ?',
          type: 'select',
          options: [
            'Approbation requise pour tous les nouveaux outils',
            'Approbation requise uniquement pour outils avec données sensibles',
            'Auto-approbation avec déclaration',
            'Pas de processus d\'approbation',
            'À déterminer',
          ],
          helpText: 'COBIT 2019 (APO12.05): Processus d\'approbation structuré pour gérer l\'adoption de nouveaux outils IA.',
        },
        {
          id: 'mesures_controle_ia',
          label: 'Quelles mesures de contrôle souhaitez-vous mettre en place ?',
          type: 'multiselect',
          options: [
            'Monitoring de l\'utilisation des outils IA',
            'Audit régulier des utilisations',
            'Formation obligatoire avant utilisation',
            'Validation humaine systématique des sorties IA',
            'Chiffrement des données envoyées aux outils IA',
            'Anonymisation des données avant utilisation',
            'Limitation des accès aux outils IA',
            'Journalisation des interactions avec l\'IA',
            'Autre',
          ],
          helpText: 'NIST AI RMF (MEASURE-1, MANAGE-3): Contrôles techniques et organisationnels pour mesurer et gérer les risques IA.',
        },
        {
          id: 'gestion_biais_ia',
          label: 'Comment souhaitez-vous gérer les risques de biais algorithmiques ?',
          type: 'select',
          options: [
            'Formation sur les biais IA obligatoire',
            'Révision humaine systématique pour décisions importantes',
            'Tests de biais avant déploiement',
            'Monitoring continu des biais',
            'Pas de mesures spécifiques',
            'À déterminer',
          ],
          helpText: 'NIST AI RMF (MEASURE-2): Gestion des biais essentielle pour l\'équité et la conformité éthique.',
        },
        {
          id: 'gestion_erreurs_ia',
          label: 'Comment souhaitez-vous gérer les erreurs et hallucinations de l\'IA ?',
          type: 'select',
          options: [
            'Révision humaine obligatoire de toutes les sorties',
            'Révision humaine pour contenus critiques uniquement',
            'Formation sur la détection d\'erreurs',
            'Processus de correction et feedback',
            'Acceptation du risque avec monitoring',
            'À déterminer',
          ],
          helpText: 'NIST AI RMF (MANAGE-4): Gestion des erreurs IA pour maintenir la qualité et la fiabilité.',
        },
        {
          id: 'conformite_loi25_ia',
          label: 'Comment gérez-vous la conformité Loi 25 pour l\'utilisation de l\'IA ?',
          type: 'multiselect',
          options: [
            'Anonymisation des données avant utilisation',
            'Consentement explicite pour données personnelles',
            'Évaluation d\'impact sur la vie privée (EIVP)',
            'Notification aux personnes concernées',
            'Contrats avec fournisseurs IA incluant clauses Loi 25',
            'Pas de mesures spécifiques',
            'À déterminer',
          ],
          helpText: 'Loi 25: Obligations légales pour la protection des renseignements personnels utilisés avec l\'IA.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Éthique & Responsabilités',
      description: 'Principes éthiques, responsabilités et formation',
      icon: 'CheckCircle',
      questions: [
        {
          id: 'principes_ethiques_ia',
          label: 'Quels principes éthiques souhaitez-vous inclure dans la politique IA ?',
          type: 'multiselect',
          options: [
            'Transparence (explicabilité des décisions IA)',
            'Équité et non-discrimination',
            'Respect de la vie privée',
            'Responsabilité et imputabilité',
            'Bienveillance (ne pas nuire)',
            'Autonomie humaine préservée',
            'Robustesse et sécurité',
            'Autre',
          ],
          helpText: 'NIST AI RMF (GOVERN-2): Principes éthiques fondamentaux pour guider l\'utilisation responsable de l\'IA.',
        },
        {
          id: 'responsabilite_utilisateurs_ia',
          label: 'Quelles sont les responsabilités des utilisateurs d\'IA ?',
          type: 'multiselect',
          options: [
            'Utiliser l\'IA de manière responsable',
            'Réviser et valider les sorties IA',
            'Ne pas utiliser de données sensibles sans autorisation',
            'Signaler les erreurs et problèmes',
            'Respecter les droits d\'auteur et propriété intellectuelle',
            'Suivre la formation obligatoire',
            'Autre',
          ],
          helpText: 'ITIL 4: Attribution claire des responsabilités pour assurer une utilisation conforme et sécurisée.',
        },
        {
          id: 'formation_ia_obligatoire',
          label: 'Souhaitez-vous rendre la formation IA obligatoire ?',
          type: 'select',
          options: ['Oui, pour tous', 'Oui, pour utilisateurs réguliers uniquement', 'Non, formation recommandée', 'À déterminer'],
          helpText: 'NIST AI RMF (GOVERN-3): Formation essentielle pour sensibiliser aux risques et bonnes pratiques IA.',
        },
        {
          id: 'contenu_formation_ia',
          label: 'Quel contenu devrait couvrir la formation IA ?',
          type: 'multiselect',
          options: [
            'Risques et limites de l\'IA',
            'Biais algorithmiques',
            'Protection des données',
            'Conformité Loi 25',
            'Détection d\'erreurs et hallucinations',
            'Utilisation responsable',
            'Outils autorisés et restrictions',
            'Procédures d\'approbation',
            'Autre',
          ],
          helpText: 'ISO 27001 (A.7.2.2): Contenu de formation structuré pour assurer la compétence des utilisateurs.',
        },
        {
          id: 'gestion_violations_politique_ia',
          label: 'Comment souhaitez-vous gérer les violations de la politique IA ?',
          type: 'select',
          options: [
            'Sanctions disciplinaires progressives',
            'Approche éducative avec correction',
            'Mixte (éducatif pour erreurs, disciplinaire pour violations intentionnelles)',
            'À déterminer',
          ],
          helpText: 'ITIL 4: Procédures claires pour gérer les violations et assurer la conformité.',
        },
        {
          id: 'propriete_intellectuelle_ia',
          label: 'Comment gérez-vous la propriété intellectuelle pour le contenu généré par IA ?',
          type: 'select',
          options: [
            'Contenu généré par IA = propriété de l\'entreprise',
            'Contenu généré par IA = propriété de l\'utilisateur',
            'Cas par cas selon l\'utilisation',
            'À déterminer',
          ],
          helpText: 'Aspect juridique important pour clarifier la propriété du contenu généré par IA.',
        },
        {
          id: 'revision_politique_ia',
          label: 'À quelle fréquence souhaitez-vous réviser la politique IA ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Lors de changements majeurs', 'À déterminer'],
          helpText: 'NIST AI RMF (GOVERN-4): Révision régulière essentielle pour s\'adapter à l\'évolution rapide de l\'IA.',
        },
      ],
    },
  ],
};





